import { Eye, EyeOff, Lock, ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PlanGate from '../../../../../../../components/gating/PlanGate';

const PartnerVisibilitySection = ({
  formData,
  handleInputChange,
  selectiveExpanded,
  setSelectiveExpanded
}) => {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 md:p-8 shadow-sm mt-5 sm:mt-8">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="w-7 h-7 sm:w-10 sm:h-10 bg-teal-50 text-primary rounded-lg sm:rounded-xl flex items-center justify-center shadow-inner shrink-0">
          {formData.hide_partners ? <EyeOff size={14} className="sm:w-5 sm:h-5" /> : <Eye size={14} className="sm:w-5 sm:h-5" />}
        </div>
        <div>
          <h3 className="text-xs sm:text-base font-semibold sm:font-bold text-slate-800 tracking-wider leading-tight">
            Partner Visibility Settings
          </h3>
          <p className="hidden sm:block text-xs text-slate-500 font-normal sm:font-medium mt-0.5">
            Control recommendation tabs shown in traveller's mobile app
          </p>
        </div>
      </div>

      <div className="border border-slate-200/80 rounded-2xl bg-white shadow-sm">
        {/* Row 1: Hide All Partners */}
        <div className="flex items-center justify-between p-5 bg-white rounded-t-2xl">
          <div className="pr-4">
            <span className="text-sm font-bold text-slate-800 block">Hide All Partners</span>
            <span className="text-[12px] text-slate-500 font-normal mt-1 leading-relaxed block">
              Completely hides the Food, Activities, and Shopping recommendation tabs in the mobile app.
            </span>
          </div>
          <PlanGate
            requiredPlan="growth"
            featureName="Hide All Partners toggle"
            tooltipText="Customizing partner visibility settings requires the Growth Plan."
            showLockIcon={false}
          >
            {({ isLocked, onClick }) => (
              <button
                type="button"
                onClick={isLocked ? onClick : () => {
                  const isChecked = !formData.hide_partners;
                  handleInputChange('hide_partners', isChecked);
                  if (isChecked) {
                    setSelectiveExpanded(false);
                  }
                }}
                className={`w-12 h-6 rounded-full transition-colors relative flex items-center shrink-0 cursor-pointer ${
                  isLocked
                    ? 'bg-slate-200 hover:bg-slate-300'
                    : formData.hide_partners ? 'bg-primary' : 'bg-slate-200'
                }`}
                title={isLocked ? 'Upgrade to Growth Plan to customize partner visibility' : undefined}
              >
                <span
                  className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow-xs flex items-center justify-center ${
                    !isLocked && formData.hide_partners ? 'right-0.5' : 'left-0.5'
                  }`}
                >
                  {isLocked && <Lock size={10} className="text-slate-500" />}
                </span>
              </button>
            )}
          </PlanGate>
        </div>

        {/* Row 2: Show Some Partners */}
        {!formData.hide_partners && (
          <>
            <div
              onClick={() => setSelectiveExpanded(!selectiveExpanded)}
              className="flex items-center justify-between p-5 border-t border-slate-100 hover:bg-slate-50/50 transition-colors cursor-pointer select-none"
            >
              <div className="pr-4">
                <span className="text-sm font-bold text-slate-800 block">Show Some Partners</span>
                <span className="text-[12px] text-slate-500 font-normal mt-1 leading-relaxed block">
                  Allow selective partners to be visible in the app.
                </span>
              </div>
              <div className="text-slate-400 p-1 hover:text-slate-600 transition-colors">
                {selectiveExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              </div>
            </div>

            {/* Sub-panel containing individual switches */}
            <AnimatePresence initial={false}>
              {selectiveExpanded && (
                <motion.div
                  className="bg-slate-50/30 border-t border-slate-100"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="p-5 space-y-6">
                    <div>
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-3">
                        Select partner category to display
                      </span>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Food */}
                        <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200/80 shadow-sm">
                          <div className="pr-2">
                            <span className="text-sm font-bold text-slate-800 block">Show Food Tab</span>
                            <span className="text-[12px] text-slate-500 font-normal">Restaurants &amp; dining</span>
                          </div>
                          <PlanGate
                            requiredPlan="growth"
                            featureName="Food Partner Visibility toggle"
                            tooltipText="Customizing partner visibility settings requires the Growth Plan."
                            showLockIcon={false}
                          >
                            {({ isLocked, onClick }) => {
                              const isActive = isLocked ? true : formData.partner_visibility?.restaurant !== false;
                              return (
                                <button
                                  type="button"
                                  onClick={isLocked ? onClick : () => handleInputChange('partner_visibility.restaurant', !isActive)}
                                  className={`w-12 h-6 rounded-full transition-colors relative flex items-center shrink-0 cursor-pointer ${
                                    isActive ? 'bg-primary' : 'bg-slate-200'
                                  }`}
                                  title={isLocked ? 'Upgrade to Growth Plan to customize partner visibility' : undefined}
                                >
                                  <span
                                    className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow-xs flex items-center justify-center ${
                                      isActive ? 'right-0.5' : 'left-0.5'
                                    }`}
                                  >
                                    {isLocked && <Lock size={10} className="text-primary" />}
                                  </span>
                                </button>
                              );
                            }}
                          </PlanGate>
                        </div>

                        {/* Activities */}
                        <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200/80 shadow-sm">
                          <div className="pr-2">
                            <span className="text-sm font-bold text-slate-800 block">Show Activities Tab</span>
                            <span className="text-[12px] text-slate-500 font-normal">Things to do</span>
                          </div>
                          <PlanGate
                            requiredPlan="growth"
                            featureName="Activities Partner Visibility toggle"
                            tooltipText="Customizing partner visibility settings requires the Growth Plan."
                            showLockIcon={false}
                          >
                            {({ isLocked, onClick }) => {
                              const isActive = isLocked ? true : formData.partner_visibility?.activity !== false;
                              return (
                                <button
                                  type="button"
                                  onClick={isLocked ? onClick : () => handleInputChange('partner_visibility.activity', !isActive)}
                                  className={`w-12 h-6 rounded-full transition-colors relative flex items-center shrink-0 cursor-pointer ${
                                    isActive ? 'bg-primary' : 'bg-slate-200'
                                  }`}
                                  title={isLocked ? 'Upgrade to Growth Plan to customize partner visibility' : undefined}
                                >
                                  <span
                                    className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow-xs flex items-center justify-center ${
                                      isActive ? 'right-0.5' : 'left-0.5'
                                    }`}
                                  >
                                    {isLocked && <Lock size={10} className="text-primary" />}
                                  </span>
                                </button>
                              );
                            }}
                          </PlanGate>
                        </div>

                        {/* Shopping */}
                        <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200/80 shadow-sm">
                          <div className="pr-2">
                            <span className="text-sm font-bold text-slate-800 block">Show Shopping Tab</span>
                            <span className="text-[12px] text-slate-500 font-normal">Shops &amp; local markets</span>
                          </div>
                          <PlanGate
                            requiredPlan="growth"
                            featureName="Shopping Partner Visibility toggle"
                            tooltipText="Customizing partner visibility settings requires the Growth Plan."
                            showLockIcon={false}
                          >
                            {({ isLocked, onClick }) => {
                              const isActive = isLocked ? true : formData.partner_visibility?.shop !== false;
                              return (
                                <button
                                  type="button"
                                  onClick={isLocked ? onClick : () => handleInputChange('partner_visibility.shop', !isActive)}
                                  className={`w-12 h-6 rounded-full transition-colors relative flex items-center shrink-0 cursor-pointer ${
                                    isActive ? 'bg-primary' : 'bg-slate-200'
                                  }`}
                                  title={isLocked ? 'Upgrade to Growth Plan to customize partner visibility' : undefined}
                                >
                                  <span
                                    className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow-xs flex items-center justify-center ${
                                      isActive ? 'right-0.5' : 'left-0.5'
                                    }`}
                                  >
                                    {isLocked && <Lock size={10} className="text-primary" />}
                                  </span>
                                </button>
                              );
                            }}
                          </PlanGate>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-slate-200 pt-5">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-3">
                        Select whose partners to display
                      </span>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Show My Partners */}
                        <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200/80 shadow-sm">
                          <div className="pr-2">
                            <span className="text-sm font-bold text-slate-800 block">Show My Partners</span>
                            <span className="text-[12px] text-slate-500 font-normal">Show partners added by you</span>
                          </div>
                          <PlanGate
                            requiredPlan="growth"
                            featureName="Show My Partners toggle"
                            tooltipText="Customizing partner visibility settings requires the Growth Plan."
                            showLockIcon={false}
                          >
                            {({ isLocked, onClick }) => {
                              const isActive = isLocked ? true : formData.partner_visibility?.show_agent_partners !== false;
                              return (
                                <button
                                  type="button"
                                  onClick={isLocked ? onClick : () => handleInputChange('partner_visibility.show_agent_partners', !isActive)}
                                  className={`w-12 h-6 rounded-full transition-colors relative flex items-center shrink-0 cursor-pointer ${
                                    isActive ? 'bg-primary' : 'bg-slate-200'
                                  }`}
                                  title={isLocked ? 'Upgrade to Growth Plan to customize partner visibility' : undefined}
                                >
                                  <span
                                    className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow-xs flex items-center justify-center ${
                                      isActive ? 'right-0.5' : 'left-0.5'
                                    }`}
                                  >
                                    {isLocked && <Lock size={10} className="text-primary" />}
                                  </span>
                                </button>
                              );
                            }}
                          </PlanGate>
                        </div>

                        {/* Show Platform Partners */}
                        <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200/80 shadow-sm">
                          <div className="pr-2">
                            <span className="text-sm font-bold text-slate-800 block">Show Trrip Partners</span>
                            <span className="text-[12px] text-slate-500 font-normal">Show default recommendations from Trrip</span>
                          </div>
                          <PlanGate
                            requiredPlan="growth"
                            featureName="Show Trrip Partners toggle"
                            tooltipText="Customizing partner visibility settings requires the Growth Plan."
                            showLockIcon={false}
                          >
                            {({ isLocked, onClick }) => {
                              const isActive = isLocked ? true : formData.partner_visibility?.show_platform_partners !== false;
                              return (
                                <button
                                  type="button"
                                  onClick={isLocked ? onClick : () => handleInputChange('partner_visibility.show_platform_partners', !isActive)}
                                  className={`w-12 h-6 rounded-full transition-colors relative flex items-center shrink-0 cursor-pointer ${
                                    isActive ? 'bg-primary' : 'bg-slate-200'
                                  }`}
                                  title={isLocked ? 'Upgrade to Growth Plan to customize partner visibility' : undefined}
                                >
                                  <span
                                    className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow-xs flex items-center justify-center ${
                                      isActive ? 'right-0.5' : 'left-0.5'
                                    }`}
                                  >
                                    {isLocked && <Lock size={10} className="text-primary" />}
                                  </span>
                                </button>
                              );
                            }}
                          </PlanGate>
                        </div>
                      </div>

                      <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-100 flex items-start gap-3">
                        <span className="text-primary text-base select-none mt-0.5">💡</span>
                        <p className="text-slate-500 text-xs font-medium leading-relaxed">
                          <strong className="text-slate-700 font-bold">How these options work:</strong> The recommendation sources selected below (e.g. your own partners or default Trrip partners) will only populate and be visible in the categories (Food, Activities, Shopping) that you have enabled above.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </div>
  );
};

export default PartnerVisibilitySection;
